import { useState } from 'react';
import { SearchSelect } from '../components/SearchSelect.js';

const OTHER_TARGET = '__other__';

export interface IqTargetSelectProps {
  /** The iQ field the answer goes to, or ''. */
  value: string;
  /** iQ fields to offer (see `iqFieldOptions`). */
  options: string[];
  /** Targets already used elsewhere on the form, marked "(already used)". */
  used: Set<string>;
  onChange: (target: string) => void;
  emptyLabel?: string;
  compact?: boolean;
  ariaLabel?: string;
}

/**
 * Which iQ field an answer goes to: iQ's own field list (searchable, it's
 * long), plus "Another iQ field…" to type one the list doesn't have (it lags
 * behind live iQ).
 */
export function IqTargetSelect({
  value,
  options,
  used,
  onChange,
  emptyLabel = 'Don’t send it to iQ',
  compact,
  ariaLabel,
}: IqTargetSelectProps) {
  // A field typed earlier (not in the list) opens as typed.
  const [typing, setTyping] = useState(() => Boolean(value) && !options.includes(value));
  const size = compact ? { height: 30, fontSize: 12 } : undefined;
  const choices = [
    { value: '', label: emptyLabel },
    ...options.map((o) => ({ value: o, label: used.has(o) && o !== value ? `${o} (already used)` : o })),
    { value: OTHER_TARGET, label: 'Another iQ field…' },
  ];
  return (
    <>
      <SearchSelect
        className={compact ? 'select mono' : 'select'}
        style={size}
        value={typing ? OTHER_TARGET : value}
        options={choices}
        alwaysShow={['', OTHER_TARGET]}
        ariaLabel={ariaLabel}
        searchPlaceholder="Search iQ fields, e.g. Email"
        onChange={(v) => {
          if (v === OTHER_TARGET) setTyping(true);
          else {
            setTyping(false);
            onChange(v);
          }
        }}
      />
      {typing && (
        <input
          className="input mono"
          style={size}
          value={value}
          placeholder="e.g. TaxSetup.EoriNumber"
          aria-label={ariaLabel ? `${ariaLabel} (typed)` : 'iQ field'}
          autoFocus
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </>
  );
}
